/**
 * Agent-file format helpers shared across surfaces (admin editor routing, chat).
 *
 * An "agent file" governs the chat experience for a context. There are two kinds:
 *  - `prompt` — a persona: a system prompt plus provider/model configuration. This is the
 *    canonical/legacy agent file and keeps the original `application/vnd.pivotalgroup.agent+json`
 *    mimetype so existing files require no migration.
 *  - `flow` — an agentic flow: a thin descriptor that references an automation document (which
 *    orchestrates one or more agent nodes). The graph itself lives in the referenced automation
 *    so it can stay encrypted at rest; the agent file never embeds the graph.
 *
 * The mimetype is authoritative when it is a recognised agent mimetype. The in-body `kind`
 * discriminator is only consulted when the mimetype is missing or unrecognised, and a mismatch
 * between a known mimetype and the body `kind` is surfaced (see {@link detectAgentKindMismatch}).
 */

export type AgentKind = "prompt" | "flow";

/**
 * Preferred resource layout for a prompt agent's takeover. `auto` defers to the runtime width-based
 * resolver; `inline`/`split` pin the resource pane. Mirrors the chat runtime's `ResourceLayout`.
 */
export type AgentLayoutPreference = "auto" | "inline" | "split";

const AGENT_LAYOUT_VALUES: readonly AgentLayoutPreference[] = ["auto", "inline", "split"];

/**
 * Normalises a raw agent-file `layout` value to a known preference, or `undefined` when absent or
 * unrecognised (the runtime then falls back to its default resolution). Shared so the editor and the
 * chat consumer agree on the accepted values.
 */
export function normalizeAgentLayout(value: unknown): AgentLayoutPreference | undefined {
  if (typeof value !== "string") return undefined;
  const normalised = value.trim().toLowerCase();
  return (AGENT_LAYOUT_VALUES as readonly string[]).includes(normalised)
    ? (normalised as AgentLayoutPreference)
    : undefined;
}

/**
 * Candidate chat input affordance for a prompt agent's governed context (drives the guided
 * assessment experience).
 *  - `freeText` — the conversational composer is available (today's default).
 *  - `guided` — the free-text composer is hidden; the candidate advances only by answering the
 *    rendered question tools (a guided, optix-style assessment flow).
 */
export type AgentInputMode = "freeText" | "guided";

/**
 * Normalises a raw agent-file `input` value to a known input mode, or `undefined` when absent or
 * unrecognised (callers then apply their own default — today `freeText`). Tolerant of casing,
 * whitespace and common spellings (`free-text`, `free_text`, `free text`).
 */
export function normalizeAgentInputMode(value: unknown): AgentInputMode | undefined {
  if (typeof value !== "string") return undefined;
  const normalised = value.trim().toLowerCase().replace(/[\s_-]+/g, "");
  if (normalised === "guided") return "guided";
  if (normalised === "freetext") return "freeText";
  return undefined;
}

/**
 * What the candidate chat does when an assessment completes (the terminal `complete` part).
 *  - `navigate` — follow the server-resolved report `navigation.url` (today's default).
 *  - `report` — render the candidate's own report in-chat from the completion `runId`/`reportFile`
 *    without redirecting away.
 */
export type AgentCompletionMode = "navigate" | "report";

const AGENT_COMPLETION_MODE_VALUES: readonly AgentCompletionMode[] = ["navigate", "report"];

/**
 * Normalises a raw agent-file `completion` value to a known completion mode, or `undefined` when
 * absent or unrecognised (callers then apply their own default — today `navigate`). Tolerant of
 * casing and whitespace.
 */
export function normalizeAgentCompletionMode(value: unknown): AgentCompletionMode | undefined {
  if (typeof value !== "string") return undefined;
  const normalised = value.trim().toLowerCase();
  return (AGENT_COMPLETION_MODE_VALUES as readonly string[]).includes(normalised)
    ? (normalised as AgentCompletionMode)
    : undefined;
}

/**
 * Normalises a raw agent-file `tools` allowlist. Returns:
 *  - `undefined` when absent (or not an array) — the agent inherits the full server tool catalogue
 *    (the backward-compatible default; no allowlist is pinned on the conversation).
 *  - a de-duplicated, trimmed `string[]` when present — the explicit allowlist. An empty array is
 *    preserved verbatim and means "no tools at all" (an explicit opt-out, distinct from absence).
 *
 * The accepted tool names are server tool identifiers (e.g. `render_text_input`, `resource_read`);
 * this helper only normalises shape, never membership — unknown names are simply ignored at dispatch.
 */
export function normalizeAgentTools(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const seen = new Set<string>();
  const result: string[] = [];
  for (const entry of value) {
    if (typeof entry !== "string") continue;
    const name = entry.trim();
    if (name.length === 0 || seen.has(name)) continue;
    seen.add(name);
    result.push(name);
  }
  return result;
}

/**
 * Normalises a raw agent-file `skills` allowlist. Returns:
 *  - `undefined` when absent (or not an array) — the agent has access to all skills
 *    (the backward-compatible default; no allowlist is pinned on the conversation).
 *  - a de-duplicated, trimmed `string[]` when present — the explicit allowlist. An empty array is
 *    preserved verbatim and means "no skills at all" (an explicit opt-out, distinct from absence).
 */
export function normalizeAgentSkills(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const seen = new Set<string>();
  const result: string[] = [];
  for (const entry of value) {
    if (typeof entry !== "string") continue;
    const name = entry.trim();
    if (name.length === 0 || seen.has(name)) continue;
    seen.add(name);
    result.push(name);
  }
  return result;
}

export const AGENT_PROMPT_MIME = "application/vnd.pivotalgroup.agent+json";
export const AGENT_FLOW_MIME = "application/vnd.pivotalgroup.agent.flow+json";

/**
 * @deprecated The tool catalog is now served dynamically by `GET /v1/chat/admin/tools`.
 * These types are kept for backwards compatibility but the hardcoded catalog is removed.
 */
export interface ChatToolCatalogEntry {
  /** Server tool identifier (matches the manifest / executable-tool name; what is persisted in `tools`). */
  name: string;
  /** Human-friendly label for the editor. */
  label: string;
  /** Short description of what the tool does. */
  description: string;
}

/** A named group of related tools for the allowlist editor. */
export interface ChatToolCatalogGroup {
  label: string;
  tools: ChatToolCatalogEntry[];
}

/** True when the mimetype is the canonical/legacy prompt-agent mimetype. */
export function isPromptAgentMimeType(mimeType?: string | null): boolean {
  return normalizeMime(mimeType) === AGENT_PROMPT_MIME;
}

/** True when the mimetype is the flow-agent mimetype. */
export function isFlowAgentMimeType(mimeType?: string | null): boolean {
  return normalizeMime(mimeType) === AGENT_FLOW_MIME;
}

/** True for any recognised agent mimetype (prompt or flow). */
export function isAgentMimeType(mimeType?: string | null): boolean {
  return isPromptAgentMimeType(mimeType) || isFlowAgentMimeType(mimeType);
}

/**
 * Resolve which editor/kind an agent file is. The mimetype wins whenever it is a recognised
 * agent mimetype; the body `kind` is a fallback only for missing/unknown mimetypes. Anything
 * unresolved defaults to `prompt` (the backward-compatible default for legacy files).
 */
export function resolveAgentKind(input: {
  mimeType?: string | null;
  content?: unknown;
}): AgentKind {
  const mime = normalizeMime(input.mimeType);
  if (mime === AGENT_FLOW_MIME) return "flow";
  if (mime === AGENT_PROMPT_MIME) return "prompt";

  const bodyKind = readBodyKind(input.content);
  return bodyKind === "flow" ? "flow" : "prompt";
}

/**
 * When the mimetype is a recognised agent mimetype but the body `kind` disagrees, returns the
 * mismatch so the UI can warn. Returns `null` when there is no recognised mimetype or no
 * conflicting body `kind`.
 */
export function detectAgentKindMismatch(
  mimeType: string | null | undefined,
  content: unknown,
): { mimeKind: AgentKind; bodyKind: AgentKind } | null {
  const mime = normalizeMime(mimeType);
  let mimeKind: AgentKind;
  if (mime === AGENT_FLOW_MIME) mimeKind = "flow";
  else if (mime === AGENT_PROMPT_MIME) mimeKind = "prompt";
  else return null;

  const bodyKind = readBodyKind(content);
  if (bodyKind && bodyKind !== mimeKind) {
    return { mimeKind, bodyKind };
  }
  return null;
}

export interface PromptAgentFile {
  kind: "prompt";
  /** Whether this agent participates in chat resolution. Absent defaults to enabled. */
  enabled?: boolean;
  system: string;
  model?: string;
  name?: string;
  agentName?: string;
  apiKey?: string;
  /**
   * Opaque reference to a system preset credential. The server resolves its protected API key;
   * agent files must never contain a copied preset key.
   */
  presetCredentialReference?: string;
  provider?: string;
  endpoint?: string;
  /** Iconify icon slug for the agent avatar (e.g. `mdi:robot-happy`). */
  icon?: string;
  defaultOpen?: boolean;
  /**
   * Auto-begin the conversation when the chat opens by streaming {@link initialMessage} without a
   * visible user turn, so the agent greets and asks the first question with no user typing. Absent or
   * `false` ⇒ the user starts the conversation themselves (today's behaviour).
   */
  autoStart?: boolean;
  /** Seed message streamed by {@link autoStart} (default `"Start"`). Never rendered as a user turn. */
  initialMessage?: string;
  /**
   * Candidate chat input affordance. `guided` hides the free-text composer (advance only by answering
   * rendered question tools); `freeText` (default when absent) keeps the conversational composer.
   */
  input?: AgentInputMode;
  /**
   * Assessment-completion behaviour. `navigate` (default when absent) follows the server-resolved
   * report `navigation.url`; `report` renders the candidate's own report in-chat from the completion
   * `runId`/`reportFile` without redirecting.
   */
  completion?: AgentCompletionMode;
  /** Show completed generic tool input/output payload cards. Absent defaults to hidden. */
  showToolDetails?: boolean;
  /** Preferred resource layout for this agent's takeover (`auto` defers to the width resolver). */
  layout?: AgentLayoutPreference;
  /**
   * Optional allowlist of server tool names this agent may use. Absent ⇒ the full server catalogue
   * (legacy default). Present ⇒ only the listed tools are offered to the model and dispatchable
   * (server-enforced); an empty array disables all tools.
   */
  tools?: string[];
  /**
   * Optional allowlist of skill names this agent may access. Absent ⇒ all skills in ~/Agents/Skills/
   * are available (legacy default). Present ⇒ only the listed skills are discoverable/readable via
   * skill tools; an empty array disables skill access entirely.
   */
  skills?: string[];
  [key: string]: unknown;
}

export interface FlowAgentFile {
  kind: "flow";
  name?: string;
  model?: string;
  provider?: string;
  endpoint?: string;
  apiKey?: string;
  /**
   * Opaque reference to a system preset credential. The server resolves its protected API key;
   * agent files must never contain a copied preset key. A non-empty `apiKey` still wins.
   */
  presetCredentialReference?: string;
  /** Path of the automation document that orchestrates this agent. */
  flowPath?: string;
  [key: string]: unknown;
}

/** Starter body for a new prompt agent file. */
export function createDefaultPromptAgent(): PromptAgentFile {
  return {
    kind: "prompt",
    system: "You are a helpful AI assistant.",
    model: "gpt-4.1-nano",
  };
}

/** Starter body for a new flow agent file. */
export function createDefaultFlowAgent(name?: string): FlowAgentFile {
  return {
    kind: "flow",
    name: name?.trim() || "Agentic flow",
    model: "",
    provider: "",
    flowPath: "",
  };
}

export interface AgentValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Lightweight structural validation of an agent file body for the given kind. Kept dependency-free
 * (no zod) so the shared chat lib stays pure; form-level UX validation lives in the editors.
 */
export function validateAgentFile(
  kind: AgentKind,
  content: unknown,
): AgentValidationResult {
  const errors: string[] = [];
  if (!isPlainObject(content)) {
    return { valid: false, errors: ["Agent file must be a JSON object."] };
  }

  const body = content as Record<string, unknown>;

  if (body.kind !== undefined && body.kind !== kind) {
    errors.push(`Body "kind" is "${String(body.kind)}" but expected "${kind}".`);
  }

  const optionalStringFields =
    kind === "prompt"
      ? ["system", "model", "name", "agentName", "apiKey", "presetCredentialReference", "provider", "endpoint", "icon", "initialMessage"]
      : ["name", "model", "provider", "endpoint", "apiKey", "presetCredentialReference", "flowPath"];

  for (const field of optionalStringFields) {
    const value = body[field];
    if (value !== undefined && value !== null && typeof value !== "string") {
      errors.push(`Field "${field}" must be a string.`);
    }
  }

  if (kind === "prompt") {
    const system = body.system;
    if (system !== undefined && system !== null && typeof system !== "string") {
      errors.push(`Field "system" must be a string.`);
    }

    const layout = body.layout;
    if (
      layout !== undefined &&
      layout !== null &&
      (typeof layout !== "string" || normalizeAgentLayout(layout) === undefined)
    ) {
      errors.push(`Field "layout" must be one of "auto", "inline", "split".`);
    }

    const input = body.input;
    if (
      input !== undefined &&
      input !== null &&
      (typeof input !== "string" || normalizeAgentInputMode(input) === undefined)
    ) {
      errors.push(`Field "input" must be one of "freeText", "guided".`);
    }

    const completion = body.completion;
    if (
      completion !== undefined &&
      completion !== null &&
      (typeof completion !== "string" || normalizeAgentCompletionMode(completion) === undefined)
    ) {
      errors.push(`Field "completion" must be one of "navigate", "report".`);
    }

    for (const boolField of ["enabled", "defaultOpen", "autoStart", "showToolDetails"]) {
      const value = body[boolField];
      if (value !== undefined && value !== null && typeof value !== "boolean") {
        errors.push(`Field "${boolField}" must be a boolean.`);
      }
    }

    const tools = body.tools;
    if (tools !== undefined && tools !== null) {
      if (!Array.isArray(tools)) {
        errors.push(`Field "tools" must be an array of tool-name strings.`);
      } else if (tools.some((entry) => typeof entry !== "string")) {
        errors.push(`Field "tools" must contain only strings.`);
      }
    }

    const skills = body.skills;
    if (skills !== undefined && skills !== null) {
      if (!Array.isArray(skills)) {
        errors.push(`Field "skills" must be an array of skill-name strings.`);
      } else if (skills.some((entry) => typeof entry !== "string")) {
        errors.push(`Field "skills" must contain only strings.`);
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

function normalizeMime(mimeType?: string | null): string {
  if (typeof mimeType !== "string") return "";
  // Strip any parameters (e.g. "; charset=utf-8") and normalise case/whitespace.
  return mimeType.split(";")[0]!.trim().toLowerCase();
}

function readBodyKind(content: unknown): AgentKind | undefined {
  if (!isPlainObject(content)) return undefined;
  const kind = (content as Record<string, unknown>).kind;
  if (kind === "flow" || kind === "prompt") return kind;
  return undefined;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
